import Icon from "@react-native-vector-icons/material-design-icons";
import { useRoute } from "@react-navigation/native";
import ContactIcon from "assets/images/defaultIcons/contact.svg";
import theme from "assets/theme";
import React, { useContext, useEffect, useReducer, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { ROLE_ADMIN, ROLE_NAMES } from "shared/constants";
import {
  attachSignedFileUrls,
  uploadFileWeb,
} from "shared/helpers/shared.helpers";
import FileService from "shared/services/File.service";
import PersonService from "shared/services/Person.service";
import {
  lookupCityStateByZip,
  lookupZipByCityState,
} from "shared/utils/address.utils";
import { mergeReplace } from "shared/utils/payload.utils";
import {
  validateEmail,
  validateMobilePhone,
  validatePhoneNumber,
  validateZipCode,
} from "shared/utils/validator.utils";
import CustomLink from "web/components/form-elements/CustomLink.component";
import Checkbox from "web/components/form-elements/inputs/Checkbox.component";
import PressableContainer from "web/components/form-elements/PressableContainer.component";
import { SCREEN_DASHBOARD_PEOPLE } from "web/constants";
import { ConfirmationContext } from "web/context/Confirmation.context";
import { actionTypes } from "web/reducers/organization.reducer";
import InlineEditableTable from "webComponentsFormElements/InlineEditableTable.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import FileUpload from "webComponentsFormElements/inputs/FileUpload.component";
import LabelInput from "webComponentsFormElements/inputs/LabelInput.component";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const PERSONAL_INFO_TOGGLE_TOOLTIP =
  "When enabled, this person's contact information will be hidden from all app people except for admins. This is useful for protecting the privacy of individuals who do not wish to have their contact information shared with others in the organization. Note: This does not affect the visibility of this person in the directory or their association with teams and locations.";

const classes = StyleSheet.create({
  formContainer: {
    flex: 1,
    padding: 20,
    flexDirection: "row",
    gap: 30,
  },
  root: {
    display: "flex",
    flexDirection: "column",
  },
  mainForm: {
    flex: 1.15,
    backgroundColor: theme.color.white,
    padding: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: theme.color.grey,
    height: "fit-content",
  },
  sidePanel: {
    flex: 1,
    gap: 20,
  },
  profilePictureContainer: {
    alignItems: "center",
    marginBottom: 20,
  },
  profilePicture: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: theme.color.grey,
    justifyContent: "center",
    alignItems: "center",
  },
  formRow: {
    flexDirection: "row",
    gap: 15,
    marginBottom: 15,
    flexWrap: "wrap",
  },
  formColumn: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginTop: 20,
    marginBottom: 10,
    color: theme.color.darkGrey,
  },
  additionalContactForm: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 10,
    padding: 10,
    backgroundColor: theme.color.lightGrey,
    borderRadius: 5,
  },
  addButton: {
    marginTop: 10,
  },
  associationBox: {
    backgroundColor: theme.color.white,
    padding: 15,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: theme.color.grey,
  },
  associationTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 10,
    color: theme.color.darkGrey,
  },
  associationList: {
    maxHeight: 120,
  },
  associationItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 15,
  },
  associationText: {
    flex: 1,
    marginRight: 10,
  },
  linkText: {
    color: theme.color.link,
    textDecorationLine: "underline",
  },
  backButtonContainer: {
    flexDirection: "row",
    alignItems: "center",
    color: theme.color.link,
  },
  emergencyContactsSection: {
    padding: 20,
    backgroundColor: theme.color.white,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: theme.color.grey,
    marginTop: 20,
  },
  warningBox: {
    backgroundColor: "#FFF9C4",
    borderWidth: 1,
    borderColor: "#FF9800",
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    flexWrap: "wrap",
  },
  warningText: {
    color: theme.color.darkGrey,
    fontSize: 14,
    flex: 1,
    lineHeight: 20,
  },
  warningIcon: {
    marginTop: 2,
  },
  errorBox: {
    backgroundColor: theme.color.toast.error,
    borderWidth: 1,
    borderColor: theme.color.red,
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    maxWidth: "100%",
  },
  errorText: {
    color: theme.color.white,
    fontSize: 14,
    flex: 1,
    lineHeight: 20,
  },
  errorIcon: {
    marginTop: 2,
  },
  addressSection: {
    padding: 20,
    backgroundColor: theme.color.white,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: theme.color.grey,
    marginTop: 20,
  },
});

const managePersonFormReducer = (state, action) => {
  switch (action.type) {
    case "SET_PERSON":
      return {
        ...state,
        ...action.payload,
        // Convert database TINYINT (0/1) to proper boolean, default to true if missing
        isContactInfoPrivate:
          action.payload.isContactInfoPrivate !== undefined
            ? Boolean(action.payload.isContactInfoPrivate)
            : true,
      };
    case "UPDATE_FIELD":
      return { ...state, [action.field]: action.value };
    case "ADD_ADDITIONAL_CONTACT":
      return {
        ...state,
        _associations: {
          ...state._associations,
          additionalContactInfo: [
            ...(state._associations?.additionalContactInfo || []),
            action.payload,
          ],
        },
      };
    case "REMOVE_ADDITIONAL_CONTACT":
      return {
        ...state,
        _associations: {
          ...state._associations,
          additionalContactInfo:
            state._associations?.additionalContactInfo?.filter(
              (additionalContactInfo, index) => index !== action.index,
            ) || [],
        },
      };
    case "UPDATE_ADDITIONAL_CONTACT":
      const updatedContacts = [
        ...(state._associations?.additionalContactInfo || []),
      ];
      updatedContacts[action.index] = {
        ...updatedContacts[action.index],
        [action.field]: action.value,
      };
      return {
        ...state,
        _associations: {
          ...state._associations,
          additionalContactInfo: updatedContacts,
        },
      };
    case "ADD_EMERGENCY_CONTACT":
      return {
        ...state,
        _associations: {
          ...state._associations,
          emergencyContacts: [
            action.payload,
            ...(state._associations?.emergencyContacts || []),
          ],
        },
      };
    case "REMOVE_EMERGENCY_CONTACT":
      return {
        ...state,
        _associations: {
          ...state._associations,
          emergencyContacts:
            state._associations?.emergencyContacts?.filter(
              (contact) => contact.emergencyContactId !== action.contactId,
            ) || [],
        },
      };
    case "UPDATE_EMERGENCY_CONTACT":
      const updatedEmergencyContacts = [
        ...(state._associations?.emergencyContacts || []),
      ];
      const contactIndex = updatedEmergencyContacts.findIndex(
        (contact) => contact.emergencyContactId === action.contactId,
      );
      if (contactIndex !== -1) {
        updatedEmergencyContacts[contactIndex] = {
          ...updatedEmergencyContacts[contactIndex],
          ...action.updates,
        };
      }
      return {
        ...state,
        _associations: {
          ...state._associations,
          emergencyContacts: updatedEmergencyContacts,
        },
      };
    default:
      return state;
  }
};

const FIELD_LABELS = {
  firstName: "First Name",
  lastName: "Last Name",
  email: "Primary Email",
  mobilePhone: "Mobile Phone",
  workPhone: "Work Phone",
  roleId: "Role",
  homeAddress: "Home Address",
  homeAddressTwo: "Home Address Line 2",
  city: "City",
  state: "State",
  position: "Position",
  zipCode: "Zip Code",
};

export default function ManagePersonScreen() {
  const route = useRoute();
  const { personId, from } = route.params || {};
  const {
    personList,
    getPersons,
    teamList,
    locationList,
    getTeams,
    getLocations,
    dispatch: orgDispatch,
  } = useContext(OrganizationContext);
  const { showToast } = useContext(ToastContext);
  const { showConfirmation } = useContext(ConfirmationContext);
  const person = personList?.find(
    (personFromList) => personFromList.personId === personId,
  );
  const [formState, dispatch] = useReducer(managePersonFormReducer, {});
  const [newContact, setNewContact] = useState({
    type: "",
    nickname: "",
    value: "",
    error: false,
  });
  const [showAddContact, setShowAddContact] = useState(false);
  const [addContactType, setAddContactType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [emergencyContactsWithoutPhone, setEmergencyContactsWithoutPhone] =
    useState([]);
  const [displayedPrimaryEmailError, setDisplayedPrimaryEmailError] =
    useState("");
  const [displayedPhoneNumberError, setDisplayedPhoneNumberError] =
    useState("");
  const [showInformationToggle, setShowInformationToggle] = useState(false);
  const [newOtherContact, setNewOtherContact] = useState({
    type: "",
    nickname: "",
    value: "",
  });
  useEffect(() => {
    if (person) {
      dispatch({ type: "SET_PERSON", payload: person });
      setIsLoading(false);
    } else {
      const fetchPeople = async () => {
        try {
          await getPersons();
          setIsLoading(false);
        } catch (error) {
          showToast("Failed to load person data", "error");
          setIsLoading(false);
        }
      };
      fetchPeople();
    }
  }, [person, getPersons, showToast]);

  useEffect(() => {
    // Update emergency contact warnings when emergency contacts change
    if (formState._associations?.emergencyContacts) {
      updateEmergencyContactWarnings(formState._associations.emergencyContacts);
    }
  }, [formState._associations?.emergencyContacts]);

  useEffect(() => {
    setIsLoading(true);
    getTeams && getTeams();
    getLocations && getLocations();
    const fetchSignedUrl = async () => {
      if (!person?.fileId) {
        setIsLoading(false);
        return;
      }
      try {
        const { payload } = await FileService.getSignedFileUrl(person.fileId);
        dispatch({
          type: "UPDATE_FIELD",
          field: "signedFileUrl",
          value: payload,
        });
        setIsLoading(false);
      } catch (error) {
        showToast("Failed to load profile picture", "error");
        setIsLoading(false);
      }
    };
    fetchSignedUrl();
  }, [getTeams, getLocations, person?.fileId, showToast]);

  const roleOptions = Object.entries(ROLE_NAMES).map(([value, label]) => ({
    value,
    label,
  }));

  const handleFieldChange = (field, value) => {
    dispatch({ type: "UPDATE_FIELD", field, value });
  };

  const handleAddContact = (type) => {
    setAddContactType(type);
    setNewContact({ type, nickname: "", value: "" });
    setShowAddContact(true);
  };

  const handleSaveContact = async () => {
    if (
      (newContact.type === "Email" &&
        newContact.value &&
        !validateEmail(newContact.value)) ||
      (newContact.type === "Phone" &&
        newContact.value &&
        !validatePhoneNumber(newContact.value))
    ) {
      setNewContact((prev) => ({ ...prev, error: true }));
      showToast(
        newContact.type === "Phone"
          ? "Please Enter a Valid Phone Number"
          : "Please Enter a Valid Email Address",
        "error",
      );
      return;
    }
    if (newContact.nickname && newContact.value) {
      const contactInfo = {
        type: newContact.type,
        nickname: newContact.nickname,
        value: newContact.value,
        personId: formState.personId,
      };
      dispatch({ type: "ADD_ADDITIONAL_CONTACT", payload: contactInfo });
      try {
        const response = await PersonService.updatePerson(formState.personId, {
          additionalContactInfo: [
            ...(formState._associations?.additionalContactInfo || []),
            contactInfo,
          ],
        });
        const updatedList = mergeReplace(
          personList,
          response.payload,
          "personId",
        );
        orgDispatch({
          type: actionTypes.SET_PERSONS,
          payload: updatedList,
        });
        showToast("Contact information added successfully", "success");
      } catch (error) {
        if (__DEV__) {
          console.error("Error adding contact information:", error);
        }
        showToast("Failed to add contact information", "error");
      }
      setNewContact({ type: "", nickname: "", value: "", error: false });
      setShowAddContact(false);
    }
  };

  const handleSaveOtherContact = async () => {
    if (
      !newOtherContact.type ||
      !newOtherContact.nickname ||
      !newOtherContact.value
    ) {
      setNewOtherContact((prev) => ({ ...prev }));
      showToast(
        "Please fill out all fields for the Additional Contact Info",
        "error",
      );
      return;
    }
    const isEmailOrPhone =
      newOtherContact.type === "Email" ||
      newOtherContact.type === "Phone" ||
      newOtherContact.type === "phone" ||
      newOtherContact.type === "email";
    if (isEmailOrPhone) {
      showToast(
        'Save email or phone types using "Add Email" or "Add Phone" buttons',
        "error",
      );
      setNewOtherContact({ type: "", nickname: "", value: "" });
      return;
    }
    const contactInfo = {
      type: newOtherContact.type,
      nickname: newOtherContact.nickname,
      value: newOtherContact.value,
      personId: formState.personId,
    };

    try {
      const response = await PersonService.updatePerson(formState.personId, {
        additionalContactInfo: [
          ...(formState._associations?.additionalContactInfo || []),
          contactInfo,
        ],
      });
      dispatch({ type: "ADD_ADDITIONAL_CONTACT", payload: contactInfo });
      const updatedList = mergeReplace(
        personList,
        response.payload,
        "personId",
      );
      orgDispatch({
        type: actionTypes.SET_PERSONS,
        payload: updatedList,
      });
      showToast("Contact information added successfully", "success");
    } catch (error) {
      if (__DEV__) {
        console.error("Error adding contact information:", error);
      }
      showToast("Failed to add contact information", "error");
    }
    setNewOtherContact({ type: "", nickname: "", value: "" });
  };

  const handleUpdateContact = async (index) => {
    const currentContact =
      formState._associations?.additionalContactInfo?.[index];
    const originalContact =
      person?._associations?.additionalContactInfo?.[index];

    if (
      (currentContact?.type === "Email" &&
        currentContact.value &&
        !validateEmail(currentContact.value)) ||
      (currentContact?.type === "Phone" &&
        currentContact.value &&
        !validatePhoneNumber(currentContact.value))
    ) {
      setFieldErrors((prev) => ({
        ...prev,
        [`additionalContactInfo_${index}`]: true,
      }));
      showToast(
        currentContact.type === "Phone"
          ? "Please Enter a Valid Phone Number"
          : "Please Enter a Valid Email Address",
        "error",
      );
      return;
    }

    const hasNoChange =
      originalContact &&
      currentContact?.nickname === originalContact.nickname &&
      currentContact?.value === originalContact.value &&
      currentContact?.type === originalContact.type;

    const isMissingField =
      !currentContact?.nickname ||
      !currentContact?.value ||
      !currentContact?.type;
    // Check if values have actually changed
    if (hasNoChange) {
      return; // No changes, don't make backend call
    } else if (isMissingField) {
      showToast(
        "Please fill out all fields for the Additional Contact Info",
        "error",
      );
      if (isMissingField) {
        dispatch({
          type: "UPDATE_ADDITIONAL_CONTACT",
          index,
          field: "value",
          value: currentContact?.value || originalContact?.value || "",
        });
        dispatch({
          type: "UPDATE_ADDITIONAL_CONTACT",
          index,
          field: "nickname",
          value: currentContact?.nickname || originalContact?.nickname || "",
        });
        dispatch({
          type: "UPDATE_ADDITIONAL_CONTACT",
          index,
          field: "type",
          value: currentContact?.type || originalContact?.type || "",
        });
        showToast(
          "Please fill out all fields for the Additional Contact Info",
          "error",
        );
      }
      return;
    }

    try {
      const response = await PersonService.updatePerson(formState.personId, {
        additionalContactInfo:
          formState._associations?.additionalContactInfo || [],
      });
      const updatedList = mergeReplace(
        personList,
        response.payload,
        "personId",
      );
      orgDispatch({
        type: actionTypes.SET_PERSONS,
        payload: updatedList,
      });
      setFieldErrors((prev) => ({
        ...prev,
        [`additionalContactInfo_${index}`]: false,
      }));
      showToast("Contact information updated successfully", "success");
    } catch (error) {
      if (__DEV__) {
        console.error("Error updating contact information:", error);
      }
      showToast("Failed to update contact information", "error");
    }
  };

  const handleRemoveContact = async (index) => {
    // Calculate the updated list BEFORE dispatching the removal
    const updatedContacts =
      formState._associations?.additionalContactInfo?.filter(
        (info, indexFromList) => indexFromList !== index,
      ) || [];

    // Update the backend first
    try {
      const response = await PersonService.updatePerson(formState.personId, {
        additionalContactInfo: updatedContacts,
      });

      // Only update local state after successful backend update
      dispatch({ type: "REMOVE_ADDITIONAL_CONTACT", index });

      const updatedList = mergeReplace(
        personList,
        response.payload,
        "personId",
      );
      orgDispatch({
        type: actionTypes.SET_PERSONS,
        payload: updatedList,
      });
      showToast("Contact information removed successfully", "success");
    } catch (error) {
      if (__DEV__) {
        console.error("Error removing contact information:", error);
      }
      showToast("Failed to remove contact information", "error");
    }
  };

  const handleSave = async (field, value) => {
    try {
      if (value === person[field]) {
        setFieldErrors((prev) => ({ ...prev, [field]: false }));
        setDisplayedPrimaryEmailError("");
        return;
      }
      if (field === "email") {
        if (value && !validateEmail(value)) {
          showToast("Please enter a valid email address", "error");
          setDisplayedPrimaryEmailError("Please enter a valid email address");
          setFieldErrors((prev) => ({ ...prev, email: true }));
          return;
        }
        if (!value) {
          setDisplayedPrimaryEmailError("Primary Email is required");
          setFieldErrors((prev) => ({ ...prev, email: true }));
          return;
        }
      }
      if (field === "mobilePhone" && value && !validateMobilePhone(value)) {
        showToast("Please enter a valid cell phone number", "error");
        setDisplayedPhoneNumberError("Please enter a valid cell phone number");
        setFieldErrors((prev) => ({ ...prev, [field]: true }));
        return;
      }
      if (field === "workPhone" && value && !validatePhoneNumber(value)) {
        showToast("Please enter a valid phone number", "error");
        setDisplayedPhoneNumberError("Please enter a valid phone number");
        setFieldErrors((prev) => ({ ...prev, [field]: true }));
        return;
      }
      const response = await PersonService.updatePerson(
        formState.personId,
        field && value !== undefined ? { [field]: value } : formState,
      );
      const updatedList = mergeReplace(
        personList,
        response.payload,
        "personId",
      );
      orgDispatch({
        type: actionTypes.SET_PERSONS,
        payload: updatedList,
      });
      setFieldErrors((prev) => ({ ...prev, [field]: false }));
      displayedPrimaryEmailError && setDisplayedPrimaryEmailError("");
      displayedPhoneNumberError && setDisplayedPhoneNumberError("");
      showToast(
        `Person ${FIELD_LABELS[field]} updated successfully`,
        "success",
      );
    } catch (error) {
      if (__DEV__) {
        console.error("Error updating person:", error);
      }
      if (error.response?.data?.message === "Email already exists.") {
        setFieldErrors((prev) => ({ ...prev, email: true }));
        setDisplayedPrimaryEmailError(
          "This Primary Email is already associated with another person.",
        );
      }
      showToast("Failed to update person", "error");
    }
  };

  const handleImageSave = async (fileBlob = null) => {
    if (!fileBlob) {
      showToast("No image selected", "error");
      return;
    }

    try {
      // Upload the image immediately
      const res = await uploadFileWeb(fileBlob);

      // Update the person record with new fileId;
      const response = await PersonService.updatePerson(formState.personId, {
        fileId: res.fileId,
      });

      // Attach signed URLs to get the new image URL
      const updatedPersonWithSignedUrl = await attachSignedFileUrls(
        response.payload,
      );

      // Update the organization context
      const updatedList = mergeReplace(
        personList,
        updatedPersonWithSignedUrl,
        "personId",
      );
      orgDispatch({
        type: actionTypes.SET_PERSONS,
        payload: updatedList,
      });

      // Update local state
      dispatch({ type: "UPDATE_FIELD", field: "fileId", value: res.fileId });
      dispatch({
        type: "UPDATE_FIELD",
        field: "signedFileUrl",
        value: updatedPersonWithSignedUrl.signedFileUrl,
      });
      showToast("Profile picture updated successfully", "success");
    } catch (error) {
      console.error("📸 Error uploading image:", error);
      if (__DEV__) {
        console.error("Error uploading image:", error);
      }
      showToast("Failed to upload profile picture", "error");
    }
  };

  const handleAddToTeam = async (teamId) => {
    try {
      const { payload } = await PersonService.addPersonToTeam(
        formState.personId,
        teamId,
      );
      const updatedList = mergeReplace(personList, payload, "personId");
      orgDispatch({
        type: actionTypes.SET_PERSONS,
        payload: updatedList,
      });
      showToast("Added to team successfully", "success");
    } catch (error) {
      if (__DEV__) {
        console.error("Error adding person to team:", error);
      }
      showToast("Failed to add to team", "error");
    }
  };

  const handleRemoveFromTeam = async (teamId) => {
    try {
      await PersonService.removePersonFromTeam(formState.personId, teamId);
      showToast("Removed from team successfully", "success");
      getPersons();
    } catch (error) {
      if (__DEV__) {
        console.error("Error removing person from team:", error);
      }
      showToast("Failed to remove from team", "error");
    }
  };

  const handleAddToLocation = async (locationId) => {
    try {
      await PersonService.addPersonToLocation(formState.personId, locationId);
      showToast("Added to location successfully", "success");
      getPersons();
    } catch (error) {
      if (__DEV__) {
        console.error("Error adding person to location:", error);
      }
      showToast("Failed to add to location", "error");
    }
  };

  const handleRemoveFromLocation = async (locationId) => {
    try {
      await PersonService.removePersonFromLocation(
        formState.personId,
        locationId,
      );
      showToast("Removed from location successfully", "success");
      getPersons();
    } catch (error) {
      if (__DEV__) {
        console.error("Error removing person from location:", error);
      }
      showToast("Failed to remove from location", "error");
    }
  };

  const handleEmergencyContactUpdate = async (contactId, updates) => {
    try {
      // For new contacts, save them and track if they don't have a primary phone
      if (contactId === "new" && updates.firstName && updates.lastName) {
        // Remove the contact from local state and create a new one via backend
        const response = await PersonService.updatePerson(formState.personId, {
          emergencyContacts: [
            ...(formState._associations?.emergencyContacts || []),
            updates,
          ],
        });

        const updatedList = mergeReplace(
          personList,
          response.payload,
          "personId",
        );
        orgDispatch({
          type: actionTypes.SET_PERSONS,
          payload: updatedList,
        });

        // Check if this contact lacks a primary phone and update warning state
        updateEmergencyContactWarnings(
          response.payload._associations?.emergencyContacts || [],
        );

        showToast("Emergency contact added successfully", "success");
        return;
      }

      // Build updated emergency contacts list first
      const updatedContacts = [
        ...(formState._associations?.emergencyContacts || []),
      ];
      const contactIndex = updatedContacts.findIndex(
        (contact) => contact.emergencyContactId === contactId,
      );
      if (contactIndex !== -1) {
        updatedContacts[contactIndex] = {
          ...updatedContacts[contactIndex],
          ...updates,
        };
      }

      const response = await PersonService.updatePerson(formState.personId, {
        emergencyContacts: updatedContacts,
      });

      // Update local state after successful backend update
      dispatch({ type: "UPDATE_EMERGENCY_CONTACT", contactId, updates });

      const updatedList = mergeReplace(
        personList,
        response.payload,
        "personId",
      );
      orgDispatch({
        type: actionTypes.SET_PERSONS,
        payload: updatedList,
      });

      // Update warning state
      updateEmergencyContactWarnings(
        response.payload._associations?.emergencyContacts || [],
      );

      showToast("Emergency contact updated successfully", "success");
    } catch (error) {
      if (__DEV__) {
        console.error("Failed to update emergency contact:", error);
      }
      showToast("Failed to update emergency contact", "error");
    }
  };

  const handleNewEmergencyContact = () => {
    const newEmergencyContact = {
      emergencyContactId: "new",
      firstName: "",
      lastName: "",
      relationship: "",
      primaryPhone: "",
      email: "",
      personId: formState.personId,
    };
    dispatch({ type: "ADD_EMERGENCY_CONTACT", payload: newEmergencyContact });
  };

  const updateEmergencyContactWarnings = (emergencyContacts) => {
    const contactsWithoutPhone = emergencyContacts.filter(
      (contact) =>
        contact.emergencyContactId !== "new" && // Don't show warning for contacts being created
        (!contact.primaryPhone || contact.primaryPhone.trim() === ""),
    );
    setEmergencyContactsWithoutPhone(contactsWithoutPhone);
  };

  const handleImageDelete = async () => {
    try {
      // Update the person record to remove fileId
      const response = await PersonService.updatePerson(formState.personId, {
        fileId: null,
      });

      // Update the organization context
      const updatedList = mergeReplace(
        personList,
        response.payload,
        "personId",
      );
      orgDispatch({
        type: actionTypes.SET_PERSONS,
        payload: updatedList,
      });

      // Update local state
      dispatch({ type: "UPDATE_FIELD", field: "fileId", value: null });
      dispatch({ type: "UPDATE_FIELD", field: "signedFileUrl", value: null });

      showToast("Profile picture removed successfully", "success");
    } catch (error) {
      console.error("📸 Error deleting image:", error);
      if (__DEV__) {
        console.error("Error deleting image:", error);
      }
      showToast("Failed to remove profile picture", "error");
    }
  };

  const handleRemoveEmergencyContact = async (contactId) => {
    try {
      if (contactId === "new") {
        // Just remove from local state if it's a new unsaved contact
        dispatch({ type: "REMOVE_EMERGENCY_CONTACT", contactId });
        return;
      }

      // Get updated emergency contacts list without the removed contact
      const updatedContacts = (
        formState._associations?.emergencyContacts || []
      ).filter((contact) => contact.emergencyContactId !== contactId);

      const response = await PersonService.updatePerson(formState.personId, {
        emergencyContacts: updatedContacts,
      });

      dispatch({ type: "REMOVE_EMERGENCY_CONTACT", contactId });

      const updatedList = mergeReplace(
        personList,
        response.payload,
        "personId",
      );
      orgDispatch({
        type: actionTypes.SET_PERSONS,
        payload: updatedList,
      });

      // Update warning state
      updateEmergencyContactWarnings(
        response.payload._associations?.emergencyContacts || [],
      );

      showToast("Emergency contact removed successfully", "success");
    } catch (error) {
      if (__DEV__) {
        console.error("Failed to remove emergency contact:", error);
      }
      showToast("Failed to remove emergency contact", "error");
    }
  };

  const personTeams = formState._associations?.Teams || [];
  const personLocations = formState._associations?.Locations || [];
  const availableTeams =
    teamList?.filter(
      (team) => !personTeams.find((pt) => pt.teamId === team.teamId),
    ) || [];
  const availableLocations =
    locationList?.filter(
      (location) =>
        !personLocations.find((pl) => pl.locationId === location.locationId),
    ) || [];

  if (isLoading) {
    return <ActivityIndicator />;
  }

  return (
    <View style={classes.root}>
      {!from && (
        <CustomLink
          linkText={
            <View style={classes.backButtonContainer}>
              <Icon name="menu-left-outline" color={"inherit"} size={20} />
              <Text>Back to People</Text>
            </View>
          }
          navigateTo={SCREEN_DASHBOARD_PEOPLE}
        />
      )}
      <View style={classes.formContainer}>
        <View style={classes.mainForm}>
          {/* Profile Picture */}
          <View style={classes.profilePictureContainer}>
            <FileUpload
              signedUrl={formState.signedFileUrl}
              setFileBlob={(blob) => {
                if (blob) {
                  handleImageSave(blob);
                } else {
                  handleImageDelete();
                }
              }}
              placeholderIcon={ContactIcon}
            />
          </View>

          {/* Basic Info */}
          <View style={classes.formRow}>
            <View style={classes.formColumn}>
              <LabelInput
                label="First Name"
                value={formState.firstName || ""}
                onChangeText={(value) => handleFieldChange("firstName", value)}
                onBlur={async () =>
                  await handleSave("firstName", formState.firstName)
                }
              />
            </View>
            <View style={classes.formColumn}>
              <LabelInput
                label="Last Name"
                value={formState.lastName || ""}
                onChangeText={(value) => handleFieldChange("lastName", value)}
                onBlur={async () =>
                  await handleSave("lastName", formState.lastName)
                }
              />
            </View>
          </View>

          <View style={classes.formRow}>
            <View style={classes.formColumn}>
              <LabelInput
                label="Position"
                value={formState.position || ""}
                onChangeText={(value) => handleFieldChange("position", value)}
                onBlur={async () =>
                  await handleSave("position", formState.position)
                }
              />
            </View>
            <View style={classes.formColumn}>
              <LabelInput
                label="Role"
                dropdown={true}
                options={roleOptions.map((role) => ({
                  key: role.value,
                  typeId: role.value,
                  label: role.label,
                }))}
                value={
                  roleOptions.find((role) => role.value === formState.roleId)
                    ?.label || ""
                }
                onSelect={async (option) => {
                  if (person?.roleId === ROLE_ADMIN) {
                    showConfirmation({
                      prompt:
                        "Are you sure you want to change this person's role? This will remove their access to the web portal.",
                      onConfirm: async () => {
                        handleFieldChange("roleId", option.key);
                        await handleSave("roleId", option.key);
                      },
                    });
                  } else {
                    handleFieldChange("roleId", option.key);
                    await handleSave("roleId", option.key);
                  }
                }}
                placeholder="Select a role"
              />
            </View>
          </View>
          <Text style={classes.sectionTitle}>Contact Information</Text>
          {/* Contact Info - Grouped by Type */}
          <View style={classes.formRow}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 5,
                width: "100%",
                flexWrap: "wrap",
              }}
            >
              <Text
                style={{
                  fontWeight: theme.font.weight.medium,
                  color: theme.color.darkGrey,
                }}
              >
                Keep personal information private
              </Text>
              <Checkbox
                checked={formState.isContactInfoPrivate || false}
                onChange={async (newValue) => {
                  // Update local state immediately for responsive UI
                  handleFieldChange("isContactInfoPrivate", newValue);
                  // Save to backend
                  try {
                    await PersonService.updatePerson(formState.personId, {
                      isContactInfoPrivate: newValue,
                    });
                    showToast(
                      "Privacy setting updated successfully",
                      "success",
                    );
                  } catch (error) {
                    // Revert local state on error
                    handleFieldChange("isContactInfoPrivate", !newValue);
                    showToast("Failed to update privacy setting", "error");
                  }
                }}
              />
              {!showInformationToggle && (
                <PressableContainer
                  onPress={() => setShowInformationToggle((prev) => !prev)}
                  style={{ alignSelf: "flex-start" }}
                >
                  <Text style={classes.linkText}>About This</Text>
                </PressableContainer>
              )}
            </View>
            {showInformationToggle && (
              <View style={[{ maxWidth: 500 }, classes.warningBox]}>
                <PressableContainer
                  onPress={() => setShowInformationToggle((prev) => !prev)}
                >
                  <Text style={classes.linkText}>Hide</Text>
                </PressableContainer>
                <Text>{PERSONAL_INFO_TOGGLE_TOOLTIP}</Text>
              </View>
            )}
          </View>

          {/* EMAIL SECTION */}
          <Text style={classes.sectionTitle}>Emails</Text>
          {/* Primary Email */}

          <LabelInput
            label="Primary Email (Login Email)"
            value={formState.email || ""}
            onChangeText={(value) => handleFieldChange("email", value)}
            onBlur={async () => {
              if (!formState.email) {
                handleFieldChange("email", person?.email || "");
              }
              await handleSave("email", formState.email);
            }}
            hasErrorOnBlur={fieldErrors.email}
            required
          />
          {displayedPrimaryEmailError ? (
            <View style={classes.errorBox}>
              <Icon name="alert-circle" size={20} color={theme.color.red} />
              <Text style={classes.errorText}>
                {displayedPrimaryEmailError}
              </Text>
            </View>
          ) : null}
          {/* Additional Emails */}
          {formState._associations?.additionalContactInfo?.map(
            (contact, index) =>
              contact.type === "Email" ? (
                <View key={index} style={classes.additionalContactForm}>
                  <View style={classes.formColumn}>
                    <LabelInput
                      label="Nickname"
                      value={contact.nickname}
                      onChangeText={(value) =>
                        dispatch({
                          type: "UPDATE_ADDITIONAL_CONTACT",
                          index,
                          field: "nickname",
                          value,
                        })
                      }
                      onBlur={() => handleUpdateContact(index)}
                      hasErrorOnBlur={
                        fieldErrors[`additionalContactInfo_${index}`]
                      }
                    />
                  </View>
                  <View style={classes.formColumn}>
                    <LabelInput
                      label="Email Address"
                      value={contact.value}
                      onChangeText={(value) =>
                        dispatch({
                          type: "UPDATE_ADDITIONAL_CONTACT",
                          index,
                          field: "value",
                          value,
                        })
                      }
                      onBlur={() => handleUpdateContact(index)}
                      hasErrorOnBlur={
                        fieldErrors[`additionalContactInfo_${index}`]
                      }
                    />
                  </View>
                  <View style={{ paddingTop: 30 }}>
                    <CustomButton
                      title="Remove"
                      variant="light"
                      onPress={() => handleRemoveContact(index)}
                    />
                  </View>
                </View>
              ) : null,
          )}

          {/* New Email Field */}
          {showAddContact && addContactType === "Email" && (
            <View style={classes.additionalContactForm}>
              <View style={classes.formColumn}>
                <LabelInput
                  label="Nickname"
                  value={newContact.nickname}
                  onChangeText={(value) =>
                    setNewContact((prev) => ({ ...prev, nickname: value }))
                  }
                  placeholder="Nickname"
                  hasErrorOnblur={newContact.error}
                />
              </View>
              <View style={classes.formColumn}>
                <LabelInput
                  label="Email Address"
                  value={newContact.value}
                  onChangeText={(value) =>
                    setNewContact((prev) => ({ ...prev, value }))
                  }
                  placeholder="example@email.com"
                  onBlur={() => handleSaveContact()}
                  hasErrorOnBlur={newContact.error}
                />
              </View>
              <View style={{ paddingTop: 30 }}>
                <CustomButton
                  title="Cancel"
                  variant="light"
                  onPress={() => setShowAddContact(false)}
                />
              </View>
            </View>
          )}

          {/* Add Email Button */}
          <View style={{ alignSelf: "flex-start" }}>
            <Text
              style={classes.linkText}
              onPress={() => handleAddContact("Email")}
            >
              + Add Other Email
            </Text>
          </View>

          {/* PHONE SECTION */}
          <Text style={classes.sectionTitle}>Phone Numbers</Text>
          {/* Primary Phones */}

          {!(person?.mobilePhone || person?.workPhone) && (
            <View style={classes.warningBox}>
              <Icon
                name="alert-circle"
                size={20}
                color={theme.color.orange || "#FF9800"}
                style={classes.warningIcon}
              />
              <Text style={classes.warningText}>
                Warning: This person has no contact number, which may cause
                issues in Business Continuity.
              </Text>
            </View>
          )}

          {displayedPhoneNumberError ? (
            <View style={classes.errorBox}>
              <Icon name="alert-circle" size={20} color={theme.color.red} />
              <Text style={classes.errorText}>{displayedPhoneNumberError}</Text>
            </View>
          ) : null}
          <View style={classes.formRow}>
            <View style={classes.formColumn}>
              <LabelInput
                label="Cell Phone"
                value={formState.mobilePhone || ""}
                onChangeText={(value) =>
                  handleFieldChange("mobilePhone", value)
                }
                onBlur={async () =>
                  await handleSave("mobilePhone", formState.mobilePhone)
                }
                hasErrorOnBlur={fieldErrors.mobilePhone}
                isPhone
              />
            </View>
            <View style={classes.formColumn}>
              <LabelInput
                label="Office Phone"
                value={formState.workPhone || ""}
                onChangeText={(value) => handleFieldChange("workPhone", value)}
                onBlur={async () =>
                  await handleSave("workPhone", formState.workPhone)
                }
                hasErrorOnBlur={fieldErrors.workPhone}
                isPhone
              />
            </View>
          </View>

          {/* Additional Phones */}
          {formState._associations?.additionalContactInfo?.map(
            (contact, index) =>
              contact.type === "Phone" ? (
                <View key={index} style={classes.additionalContactForm}>
                  <View style={classes.formColumn}>
                    <LabelInput
                      label="Nickname"
                      value={contact.nickname}
                      onChangeText={(value) =>
                        dispatch({
                          type: "UPDATE_ADDITIONAL_CONTACT",
                          index,
                          field: "nickname",
                          value,
                        })
                      }
                      onBlur={() => handleUpdateContact(index)}
                    />
                  </View>
                  <View style={classes.formColumn}>
                    <LabelInput
                      label="Phone Number"
                      value={contact.value}
                      onChangeText={(value) =>
                        dispatch({
                          type: "UPDATE_ADDITIONAL_CONTACT",
                          index,
                          field: "value",
                          value,
                        })
                      }
                      onBlur={() => handleUpdateContact(index)}
                      hasErrorOnBlur={
                        fieldErrors[`additionalContactInfo_${index}`]
                      }
                      isPhone
                    />
                  </View>
                  <View style={{ paddingTop: 30 }}>
                    <CustomButton
                      title="Remove"
                      variant="light"
                      onPress={() => handleRemoveContact(index)}
                    />
                  </View>
                </View>
              ) : null,
          )}

          {/* New Phone Field */}
          {showAddContact && addContactType === "Phone" && (
            <View style={classes.additionalContactForm}>
              <View style={classes.formColumn}>
                <LabelInput
                  label="Nickname"
                  value={newContact.nickname}
                  onChangeText={(value) =>
                    setNewContact((prev) => ({ ...prev, nickname: value }))
                  }
                  placeholder="Nickname"
                  hasErrorOnblur={newContact.error}
                />
              </View>
              <View style={classes.formColumn}>
                <LabelInput
                  label="Phone Number"
                  value={newContact.value}
                  onChangeText={(value) =>
                    setNewContact((prev) => ({ ...prev, value }))
                  }
                  onBlur={() => handleSaveContact()}
                  hasErrorOnBlur={newContact.error}
                  isPhone
                />
              </View>
              <View style={{ paddingTop: 30 }}>
                <CustomButton
                  title="Cancel"
                  variant="light"
                  onPress={() => setShowAddContact(false)}
                />
              </View>
            </View>
          )}

          {/* Add Phone Button */}
          <View style={{ alignSelf: "flex-start" }}>
            <Text
              style={classes.linkText}
              onPress={() => handleAddContact("Phone")}
            >
              + Add Other Phone
            </Text>
          </View>
        </View>
        {/* Side Panel - Teams and Locations */}
        <View style={classes.sidePanel}>
          {/* Teams */}
          <View style={classes.associationBox}>
            <Text style={classes.associationTitle}>Teams</Text>
            <Text style={{ fontSize: 14, fontWeight: "bold", marginBottom: 5 }}>
              Member of:
            </Text>
            <ScrollView style={classes.associationList}>
              {personTeams.map((team) => (
                <View key={team.teamId} style={classes.associationItem}>
                  <Text style={classes.associationText}>{team.name}</Text>
                  <Text
                    style={classes.linkText}
                    onPress={() => handleRemoveFromTeam(team.teamId)}
                  >
                    Remove
                  </Text>
                </View>
              ))}
            </ScrollView>

            <Text
              style={{
                fontSize: 14,
                fontWeight: "bold",
                marginBottom: 5,
                marginTop: 15,
              }}
            >
              Available Teams:
            </Text>
            <ScrollView style={classes.associationList}>
              {availableTeams.map((team) => (
                <View key={team.teamId} style={classes.associationItem}>
                  <Text style={classes.associationText}>{team.name}</Text>
                  <Text
                    style={classes.linkText}
                    onPress={() => handleAddToTeam(team.teamId)}
                  >
                    Add
                  </Text>
                </View>
              ))}
            </ScrollView>
          </View>

          {/* Locations */}
          <View style={classes.associationBox}>
            <Text style={classes.associationTitle}>Places</Text>
            <Text style={{ fontSize: 14, fontWeight: "bold", marginBottom: 5 }}>
              Assigned to:
            </Text>
            <ScrollView style={classes.associationList}>
              {personLocations.map((location) => (
                <View key={location.locationId} style={classes.associationItem}>
                  <Text style={classes.associationText}>{location.name}</Text>
                  <Text
                    style={classes.linkText}
                    onPress={() =>
                      handleRemoveFromLocation(location.locationId)
                    }
                  >
                    Remove
                  </Text>
                </View>
              ))}
            </ScrollView>

            <Text
              style={{
                fontSize: 14,
                fontWeight: "bold",
                marginBottom: 5,
                marginTop: 15,
              }}
            >
              Available Places:
            </Text>
            <ScrollView style={classes.associationList}>
              {availableLocations.map((location) => (
                <View key={location.locationId} style={classes.associationItem}>
                  <Text style={classes.associationText}>{location.name}</Text>
                  <Text
                    style={classes.linkText}
                    onPress={() => handleAddToLocation(location.locationId)}
                  >
                    Add
                  </Text>
                </View>
              ))}
            </ScrollView>
          </View>
          {/* Additional Contact Info */}
          <View style={classes.associationBox}>
            <Text style={classes.associationTitle}>
              Additional Contact Info
            </Text>
            {formState._associations?.additionalContactInfo?.map(
              (contact, index) =>
                contact.type !== "Phone" && contact.type !== "Email" ? (
                  <View key={index} style={classes.additionalContactForm}>
                    <View style={classes.formColumn}>
                      <LabelInput
                        label="Type"
                        value={contact.type}
                        onChangeText={(value) =>
                          dispatch({
                            type: "UPDATE_ADDITIONAL_CONTACT",
                            index,
                            field: "type",
                            value,
                          })
                        }
                        onBlur={() => handleUpdateContact(index)}
                        required
                      />
                    </View>
                    <View style={classes.formColumn}>
                      <LabelInput
                        label="Nickname"
                        value={contact.nickname}
                        onChangeText={(value) =>
                          dispatch({
                            type: "UPDATE_ADDITIONAL_CONTACT",
                            index,
                            field: "nickname",
                            value,
                          })
                        }
                        required
                        onBlur={() => handleUpdateContact(index)}
                      />
                    </View>
                    <View style={classes.formColumn}>
                      <LabelInput
                        label="Value"
                        value={contact.value}
                        onChangeText={(value) =>
                          dispatch({
                            type: "UPDATE_ADDITIONAL_CONTACT",
                            index,
                            field: "value",
                            value,
                          })
                        }
                        hasErrorOnBlur={
                          fieldErrors[`additionalContactInfo_${index}`]
                        }
                        required
                        onBlur={() => handleUpdateContact(index)}
                      />
                    </View>
                    <View style={{ paddingTop: 30 }}>
                      <CustomButton
                        title="Remove"
                        variant="light"
                        onPress={() => handleRemoveContact(index)}
                      />
                    </View>
                  </View>
                ) : null,
            )}
            <View style={classes.additionalContactForm}>
              <View style={classes.formColumn}>
                <LabelInput
                  label="Type"
                  value={newOtherContact.type}
                  onChangeText={(value) =>
                    setNewOtherContact((prev) => ({ ...prev, type: value }))
                  }
                  placeholder="Slack, LinkedIn, etc."
                  required
                />
              </View>
              <View style={classes.formColumn}>
                <LabelInput
                  label="Nickname"
                  value={newOtherContact.nickname}
                  onChangeText={(value) =>
                    setNewOtherContact((prev) => ({ ...prev, nickname: value }))
                  }
                  placeholder="John Doe's Slack"
                  required
                />
              </View>
              <View style={classes.formColumn}>
                <LabelInput
                  label="Value"
                  value={newOtherContact.value}
                  onChangeText={(value) =>
                    setNewOtherContact((prev) => ({ ...prev, value }))
                  }
                  placeholder="@john.Doe"
                  required
                />
              </View>
              <View style={{ paddingTop: 30 }}>
                <CustomButton
                  title="Confirm"
                  variant="light"
                  onPress={() => handleSaveOtherContact()}
                  icon="check"
                />
              </View>
            </View>
          </View>
        </View>
      </View>
      <View style={classes.addressSection}>
        {/* Address */}
        <Text style={classes.sectionTitle}>Address</Text>
        <View style={classes.formRow}>
          <View>
            <LabelInput
              label="Zip Code"
              value={formState.zipCode || ""}
              numbersOnly
              onChangeText={(value) => {
                if (value.length > 5) {
                  return;
                }
                handleFieldChange("zipCode", value);
              }}
              onBlur={async () => {
                if (formState.zipCode) {
                  if (!validateZipCode(formState.zipCode)) {
                    showToast("Please enter a valid zip code", "error");
                    setFieldErrors((prev) => ({ ...prev, zipCode: true }));
                    return;
                  }
                  try {
                    const cityState = await lookupCityStateByZip(
                      formState.zipCode,
                    );
                    if (cityState) {
                      handleFieldChange("city", cityState.city);
                      handleFieldChange("state", cityState.state);
                      await handleSave("city", cityState.city);
                      await handleSave("state", cityState.state);
                    }
                  } catch (error) {
                    if (
                      error.status === 404 &&
                      error.response?.data?.message === "ZIP code not found"
                    ) {
                      showToast(
                        "Zip code not found. Try again or enter City/State manually.",
                        "error",
                      );
                    }
                  } finally {
                    await handleSave("zipCode", formState.zipCode);
                  }
                }
              }}
              hasErrorOnBlur={fieldErrors.zipCode}
            />
          </View>
        </View>
        <LabelInput
          label="Home Address"
          value={formState.homeAddress || ""}
          onChangeText={(value) => handleFieldChange("homeAddress", value)}
          onBlur={async () =>
            await handleSave("homeAddress", formState.homeAddress)
          }
        />
        <LabelInput
          label="Home Address 2"
          value={formState.homeAddressTwo || ""}
          onChangeText={(value) => handleFieldChange("homeAddressTwo", value)}
          onBlur={async () =>
            await handleSave("homeAddressTwo", formState.homeAddressTwo)
          }
        />
        {formState.zipCode?.length === 5 && (
          <View style={classes.formRow}>
            <View style={{ flexShrink: 1 }}>
              <LabelInput
                label="City"
                value={formState.city || ""}
                onChangeText={(value) => handleFieldChange("city", value)}
                onBlur={async () => {
                  await handleSave("city", formState.city);
                  if (formState.city && formState.state && !formState.zipCode) {
                    try {
                      const zip = await lookupZipByCityState(
                        formState.city,
                        formState.state,
                      );
                      if (zip) {
                        handleFieldChange("zipCode", zip);
                        await handleSave("zipCode", zip);
                      }
                    } catch (error) {
                      if (
                        error.status === 404 &&
                        error.response?.data?.message ===
                          "No ZIP codes found for city/state"
                      ) {
                        showToast(
                          "City/State combination not found. Try again or enter Zip code manually.",
                          "error",
                        );
                      }
                    }
                  }
                }}
              />
            </View>
            <View style={{ flexShrink: 1 }}>
              <LabelInput
                label="State"
                value={formState.state || ""}
                onChangeText={(value) => {
                  if (value.length > 2) {
                    return;
                  }
                  handleFieldChange("state", value);
                }}
                onBlur={async () => {
                  await handleSave("state", formState.state);
                  if (formState.city && formState.state && !formState.zipCode) {
                    try {
                      const zip = await lookupZipByCityState(
                        formState.city,
                        formState.state,
                      );
                      if (zip) {
                        handleFieldChange("zipCode", zip);
                        await handleSave("zipCode", zip);
                      }
                    } catch (error) {
                      if (
                        error.status === 404 &&
                        error.response?.data?.message ===
                          "No ZIP codes found for city/state"
                      ) {
                        showToast(
                          "City/State combination not found. Try again or enter Zip code manually.",
                          "error",
                        );
                      }
                    }
                  }
                }}
              />
            </View>
          </View>
        )}
      </View>
      {/* Emergency Contacts Section */}
      <View style={classes.emergencyContactsSection}>
        <Text style={classes.sectionTitle}>Emergency Contacts</Text>

        {/* Warning Box for contacts without primary phone */}
        {emergencyContactsWithoutPhone.length > 0 && (
          <View style={classes.warningBox}>
            <Icon
              name="alert-circle"
              size={20}
              color={"#FF9800"}
              style={classes.warningIcon}
            />
            <Text style={classes.warningText}>
              Warning: The following emergency contact
              {emergencyContactsWithoutPhone.length > 1
                ? "s have"
                : " has"}{" "}
              been created without a primary contact number:{" "}
              {emergencyContactsWithoutPhone.map((contact, index) => (
                <Text
                  key={contact.emergencyContactId}
                  style={{ fontWeight: "bold" }}
                >
                  {contact.firstName} {contact.lastName}
                  {index < emergencyContactsWithoutPhone.length - 1 ? ", " : ""}
                </Text>
              ))}
            </Text>
          </View>
        )}
        <InlineEditableTable
          data={(formState._associations?.emergencyContacts || []).map(
            (contact) => ({
              id: contact.emergencyContactId,
              firstName: contact.firstName,
              lastName: contact.lastName,
              relationship: contact.relationship,
              primaryPhone: contact.primaryPhone,
              secondaryPhone: contact.secondaryPhone,
              email: contact.email,
              secondaryEmail: contact.secondaryEmail,
              _rawContact: contact,
            }),
          )}
          columns={[
            //IMPLEMENT REQUIRED TYPE
            {
              key: "firstName",
              label: "First Name",
              editable: true,
              type: "textRequired",
              errorMessage: "First name is required",
              sortable: true,
            },
            {
              key: "lastName",
              label: "Last Name",
              editable: true,
              type: "textRequired",
              errorMessage: "Last name is required",
              sortable: true,
            },
            {
              key: "relationship",
              label: "Relationship",
              editable: true,
              type: "text",
            },
            {
              key: "primaryPhone",
              label: "Primary Phone",
              editable: true,
              type: "textValidation",
              validationFn: validatePhoneNumber,
              errorMessage: "Invalid primary phone number",
              isPhone: true,
            },
            {
              key: "secondaryPhone",
              label: "Secondary Phone",
              editable: true,
              type: "textValidation",
              validationFn: validatePhoneNumber,
              errorMessage: "Invalid secondary phone number",
              isPhone: true,
            },
            {
              key: "email",
              label: "Primary Email",
              editable: true,
              type: "textValidation",
              validationFn: validateEmail,
              errorMessage: "Invalid primary email address",
            },
            {
              key: "secondaryEmail",
              label: "Secondary Email",
              editable: true,
              type: "textValidation",
              validationFn: validateEmail,
              errorMessage: "Invalid secondary email address",
            },
            { key: "delete", label: "", editable: false, type: "action" },
          ]}
          searchPlaceholder="Search emergency contacts..."
          onRowUpdate={handleEmergencyContactUpdate}
          onNewContact={handleNewEmergencyContact}
          onRemoveContact={handleRemoveEmergencyContact}
        />
      </View>
    </View>
  );
}
